import React from "react";
import { Title, TitleProps, useMantineTheme } from "@mantine/core";

interface Heading1Props extends Omit<TitleProps, "order" | "styles"> {
  children: React.ReactNode;
  variant?: "default" | "shadow" | "gradient" | "hero";
  className?: string;
  style?: React.CSSProperties;
}

/**
 * Heading1 - Styled H1 component
 * Usage: <Heading1>Main Title</Heading1>
 * Usage with shadow: <Heading1 variant="shadow">Main Title</Heading1>
 * Usage with gradient: <Heading1 variant="gradient">Main Title</Heading1>
 * Usage on photo bg: <Heading1 variant="hero">Main Title</Heading1>
 */
export const Heading1: React.FC<Heading1Props> = ({
  children,
  variant = "default",
  className,
  style,
  ...props
}) => {
  const theme = useMantineTheme();
  const other = theme.other as Record<string, string>;

  const getTextShadow = () => {
    if (variant === "shadow") {
      return "0 2px 8px rgba(0, 0, 0, 0.5), 0 4px 16px rgba(0, 0, 0, 0.3)";
    }
    if (variant === "hero") {
      return "0 2px 4px rgba(0, 0, 0, 0.55), 0 8px 24px rgba(0, 0, 0, 0.4)";
    }
    return undefined;
  };

  // "gradient" variant retained as a prop for backwards compat across
  // 9 pages, but the actual gradient treatment was removed 2026-07-23
  // after feedback + design-is audit flagged it as a 2020-2024 trend
  // marker under #7 Long-lasting. Hero H1s now render as solid warm
  // ivory — the calm typographic anchor. Pillar accents carry the
  // color story elsewhere on the page.
  const getGradientStyles = (): React.CSSProperties => {
    // Suppress the unused-variable warning on `other` — kept in scope
    // in case we bring back a subtler treatment later.
    void other;
    return {};
  };

  const heroStyles: React.CSSProperties =
    variant === "hero"
      ? {
          fontSize: "clamp(2.5rem, 7vw, 5.25rem)",
          fontWeight: 900,
          letterSpacing: "-0.02em",
          lineHeight: 1.05,
        }
      : {};

  return (
    <Title
      order={1}
      className={className}
      style={{
        color: other.textPrimary,
        fontWeight: 800,
        textShadow: getTextShadow(),
        ...getGradientStyles(),
        ...heroStyles,
        ...style,
      }}
      {...props}
    >
      {children}
    </Title>
  );
};

export default Heading1;
